import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center p-1 rounded-full border text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden px-3 py-1 text-sm font-medium",
  {
    variants: {
      variant: {
        default: "bg-blue-50 text-blue-700  border border-blue-100",
        primary: "bg-blue-50 text-blue-700  border border-blue-100",
        secondary: "border-transparent bg-gray-100 text-gray-800",
        danger:
          "border-transparent bg-red-100 text-red-800 [a&]:hover:bg-red-200",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground border border-primary",
        success:
          "border-transparent bg-green-100 text-green-800 [a&]:hover:bg-green-200",
        warning:
          "border-transparent bg-yellow-100 text-yellow-800 [a&]:hover:bg-yellow-200",
        orange:
          "text-white border-transparent bg-orange-500  [a&]:hover:bg-orange-600",
        successOutline:
          "text-[var(--success)] border-[var(--success)] bg-transparent [a&]:hover:bg-[var(--success)] [a&]:hover:text-white",
        errorOutline:
          "text-destructive border-destructive bg-transparent [a&]:hover:bg-destructive [a&]:hover:text-white",
        error: "bg-red-100 text-red-700 border-red-200 hover:bg-red-50",
        
        // Solid (bg-500 style) variants
        solidBlue: "border-transparent bg-blue-600 text-white hover:bg-blue-700",
        solidGreen: "border-transparent bg-emerald-600 text-white hover:bg-emerald-700",
        solidOrange: "border-transparent bg-orange-500 text-white hover:bg-orange-600",
        solidAmber: "border-transparent bg-amber-500 text-white hover:bg-amber-600",
        solidRed: "border-transparent bg-red-600 text-white hover:bg-red-700",
        solidIndigo: "border-transparent bg-indigo-600 text-white hover:bg-indigo-700",
        solidTeal: "border-transparent bg-teal-600 text-white hover:bg-teal-700",
        solidPurple: "border-transparent bg-purple-600 text-white hover:bg-purple-700",

        // Light (bg-200 style) variants
        lightBlue: "bg-blue-100 text-blue-900 border-blue-200/60 hover:bg-blue-200",
        lightGreen: "bg-emerald-100 text-emerald-900 border-emerald-200/60 hover:bg-emerald-200",
        lightOrange: "bg-orange-100 text-orange-900 border-orange-200/60 hover:bg-orange-200",
        lightAmber: "bg-amber-100 text-amber-950 border-amber-200/60 hover:bg-amber-200",
        lightRed: "bg-red-100 text-red-900 border-red-200/60 hover:bg-red-200",
        lightIndigo: "bg-indigo-100 text-indigo-900 border-indigo-200/60 hover:bg-indigo-200",
        lightTeal: "bg-teal-100 text-teal-900 border-teal-200/60 hover:bg-teal-200",
        lightPurple: "bg-purple-100 text-purple-900 border-purple-200/60 hover:bg-purple-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(
        badgeVariants({ variant }),
        `${className} cursor-pointer text-xs rounded-md px-2.5 py-0.5 font-semibold border shadow-none `,
      )}
      {...props}
    />
  );
}

const statusStyleMap: Record<string, string> = {
  PENDING: "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-50",
  APPROVED_NOT_CREATED_SAP:
    "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  APPROVED_CREATED_SAP:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  SAP_LINKED: "bg-green-50 text-green-700 border-green-200 hover:bg-green-50",
  REJECTED: "bg-red-50 text-red-700 border-red-200 hover:bg-red-50",
  SENT: "bg-green-50 text-green-700 border-green-200 hover:bg-green-50",
  RECEIVED: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  FAILED: "bg-red-50 text-red-700 border-red-200 hover:bg-red-50",
  CANCELLED: "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-50",
  ACTIVE:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  ACTIVO:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  ENABLED:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  HABILITADO:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  VIGENTE:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  EXPIRADA: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  EXPIRED: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  INACTIVE: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  INACTIVO: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  DISABLED: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  SUSPENDIDO: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  READY:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  LISTO:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  AVAILABLE:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  DISPONIBLE:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  ACTUAL:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  CURRENT:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  STALE:
    "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50",
  CACHE_DESACTUALIZADA:
    "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50",
  NO_DISPONIBLE:
    "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  UNAVAILABLE:
    "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  ANTERIOR:
    "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100",
  SUPERSEDED:
    "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100",
  NO_PRICE:
    "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50",
  SIN_PRECIO:
    "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50",
  NOT_FOUND:
    "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100",
  NO_EN_CATALOGO:
    "bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-100",
  ERROR:
    "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  NOT_READY: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  NO_LISTO: "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-50",
  NO_CONFIABLE:
    "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50",
  UNRELIABLE:
    "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50",
  EN_PREPARACION:
    "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-50",
  SIN_PROCESAR:
    "bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-50",
  PROCESADO: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  EN_TRANSITO:
    "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-50",
  ENTREGADO:
    "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50",
  SIN_ENTREGA:
    "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-50",
  EN_PROGRESO:
    "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  IN_PROGRESS:
    "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  REFRESH_IN_PROGRESS:
    "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50",
  SIN_PROCESO:
    "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-50",
  IDLE:
    "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-50",
};

interface StatusBadgeProps extends React.ComponentProps<typeof Badge> {
  status: string;
  label?: string;
}

function StatusBadge({ status, label, className, ...props }: StatusBadgeProps) {
  const normalizedStatus = String(status || "").toUpperCase();
  const badgeClass =
    statusStyleMap[normalizedStatus] ||
    "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-50";

  return (
    <Badge
      className={cn(
        "px-2.5 py-0.5  text-xs font-semibold border shadow-none",
        badgeClass,
        className,
      )}
      {...props}
    >
      {label || status}
    </Badge>
  );
}

export { Badge, StatusBadge, badgeVariants };
